import generatedAppEnv from 'app-env-generated';
import * as yup from 'yup';
import {
  getWindowLocationEnvironment,
  isProductionEnvironment,
  isStagingEnvironment,
} from './env.util';

export type ENV = {
  AGENT_CHAT_MESSAGES_ENDPOINT: string;
  VISUALISATION_AGENT_ENDPOINT: string;
  GRAPHQL_API_ENDPOINT: string;
  SOCKET_ENDPOINT: string;
  AUTH_SERVER_URL: string;
  AUTH_REALM: string;
  AUTH_CLIENT_ID: string;
  LOGGING_ENABLED: boolean;
  LOGGING_WITHOUT_TRACE_ID_ENABLED: boolean;
  LOGGING_CLIENT_TOKEN: string;
  TRACKING_ENABLED: boolean;
  TRACKING_ID: number;
  ANALYTICS_ENABLED: boolean;
  ANALYTICS_API_TOKEN: string;
  ANALYTICS_API_HOST?: Maybe<string>;
  FEATURE_TOGGLES_FEATURES_ENDPOINT: string;
  FEATURE_TOGGLES_DEV_MODE_ENABLED: boolean;
  PAYMENT_SCRIPT_URL: string;
  PAYMENT_PUBLISHABLE_KEY: string;
  PAYMENT_PRICING_TABLE_ID: string;
  USER_GUIDANCE_WORKSPACE_CODE: string;
  RUM_ENABLED?: Maybe<boolean>;
  RUM_APPLICATION_ID?: Maybe<string>;
  RUM_CLIENT_TOKEN?: Maybe<string>;
  RUM_SITE?: Maybe<string>;
  RUM_SERVICE?: Maybe<string>;
  RUM_SESSION_SAMPLE_RATE?: Maybe<number>;
  RUM_TRACE_SAMPLE_RATE?: Maybe<number>;
  RUM_SESSION_REPLAY_SAMPLE_RATE?: Maybe<number>;
};

const envSchema: yup.Schema<ENV> = yup.object({
  AGENT_CHAT_MESSAGES_ENDPOINT: yup.string().required(),
  VISUALISATION_AGENT_ENDPOINT: yup.string().required(),
  GRAPHQL_API_ENDPOINT: yup.string().required(),
  SOCKET_ENDPOINT: yup.string().required(),
  AUTH_SERVER_URL: yup.string().required(),
  AUTH_REALM: yup.string().required(),
  AUTH_CLIENT_ID: yup.string().required(),
  LOGGING_ENABLED: yup.boolean().required(),
  LOGGING_WITHOUT_TRACE_ID_ENABLED: yup.boolean().required(),
  LOGGING_CLIENT_TOKEN: yup.string().required(),
  TRACKING_ENABLED: yup.boolean().required(),
  TRACKING_ID: yup.number().required(),
  ANALYTICS_ENABLED: yup.boolean().required(),
  ANALYTICS_API_TOKEN: yup.string().required(),
  ANALYTICS_API_HOST: yup.string().optional(),
  FEATURE_TOGGLES_FEATURES_ENDPOINT: yup.string().required(),
  FEATURE_TOGGLES_DEV_MODE_ENABLED: yup.boolean().required(),
  PAYMENT_SCRIPT_URL: yup.string().required(),
  PAYMENT_PUBLISHABLE_KEY: yup.string().required(),
  PAYMENT_PRICING_TABLE_ID: yup.string().required(),
  USER_GUIDANCE_WORKSPACE_CODE: yup.string().required(),
  RUM_ENABLED: yup.boolean().optional(),
  RUM_APPLICATION_ID: yup.string().optional(),
  RUM_CLIENT_TOKEN: yup.string().optional(),
  RUM_SITE: yup.string().optional(),
  RUM_SERVICE: yup.string().optional(),
  RUM_SESSION_SAMPLE_RATE: yup.number().optional(),
  RUM_TRACE_SAMPLE_RATE: yup.number().optional(),
  RUM_SESSION_REPLAY_SAMPLE_RATE: yup.number().optional(),
});

const appEnv: ENV = { ...generatedAppEnv };

const getConfigEndpoint = (env: string) =>
  `${generatedAppEnv.ENVIRONMENTAL_VARIABLES_ENDPOINT}?app=${generatedAppEnv.APP_NAME}&environment=${env}`;

export const hydrateAppEnv = async (): Promise<void> => {
  if (!isProductionEnvironment() && !isStagingEnvironment()) return;
  const locationEnv = getWindowLocationEnvironment();
  const endpoint = getConfigEndpoint(locationEnv);
  const response = await fetch(endpoint);
  if (!response.ok) throw new Error('Failed to fetch config env');
  const data = await response.json();
  const validated = await envSchema.validate(
    { ...generatedAppEnv, ...data },
    { stripUnknown: true }
  );
  Object.assign(appEnv, validated);
};

export default appEnv;
